import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { AgentHeader } from '@/components/agent/AgentHeader';
import { BirthdayBanner } from '@/components/agent/BirthdayBanner';
import {
  AgentSnapshot,
  AgentInformation,
  AgentCarrierPortfolio,
  AgentActivityTimeline,
  AgentCommunicationException,
} from '@/components/agent/blocks';
import { AgentContractsTable } from '@/components/agent/blocks/AgentContractsTable';
import { SmartComplianceTracker } from '@/components/common/SmartComplianceTracker';
import {
  EditAgentDialog,
  ContractManageDialog,
  ChangeNPNDialog,
  DeleteAppointmentDialog,
  DeleteRequestDialog,
  TransferAgentDialog,
  GenerateAccessDialog,
  DisableEnableAccessDialog,
  ManageUserDialog,
  UpdateInfoLinkDialog,
} from '@/components/agent/dialogs';
import { GettingStartedChecklist } from '@/components/agents/GettingStartedChecklist';
import { OnboardingPanel } from '@/components/agents/OnboardingPanel';
import { AepCertificationsTab } from '@/components/aep/AepCertificationsTab';
import { AgentAcademyBadges } from '@/components/agent/AgentAcademyBadges';
import { AgentDocumentsTab } from '@/components/agent/AgentDocumentsTab';
import ContactSmsCard from '@/components/sms/ContactSmsCard';
import { HubspotSyncCard } from '@/components/agent/HubspotSyncCard';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, MessageSquare, ExternalLink } from 'lucide-react';
import {
  agentAPI, agencyAPI, contractRequestAPI, appointmentAPI,
  carrierAPI, contractTypeAPI, levelAPI,
} from '@/lib/api';
import { isKRRole, isAgent, isAccountingRole } from '@/lib/utils';
import { CRMNotesPanel } from '@/components/crm/CRMNotesPanel';
import PaymentInformationCard from '@/components/payment/PaymentInformationCard';
import PaymentHistoryCard from '@/components/payment/PaymentHistoryCard';
import PayeeEftCard from '@/components/payment/PayeeEftCard';
import RecipientAccountingSection from '@/components/accounting/RecipientAccountingSection';
import RecipientStatementsCard from '@/components/accounting/RecipientStatementsCard';

/**
 * AgentProfilePage — pure orchestrator for the Agent Command Center.
 *
 * Responsibilities (kept intentionally small):
 *   1. Fetch agent, agency, appointments, requests, hierarchy, linked user.
 *   2. Resolve permissions (isKR).
 *   3. Toggle dialog visibility flags and pass down refresh callbacks.
 *   4. Compose the header + dashboard blocks + dialogs.
 *
 * The page contains ONLY the licensing/identity workspace surface — Identity,
 * Snapshot, Contracting Pipeline, Carrier Portfolio, Compliance, Documents,
 * Timeline and Administrative Actions.
 */
export default function AgentProfilePage() {
  const { npn } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [agency, setAgency] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [hierarchy, setHierarchy] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [linkedUser, setLinkedUser] = useState(null);
  // Phase 27.38 — Bump this key to force PaymentInformationCard to remount
  // (and re-fetch) after an EFT approval so the "Direct Deposit" flag flips
  // to Complete without the admin having to reload the page.
  const [paymentInfoRefreshKey, setPaymentInfoRefreshKey] = useState(0);

  // Phase 27.6 — Section memory: remember which accordion panels each
  // user leaves open, per-browser. Keyed by user_id so different admins
  // on the same machine keep their own layout. Falls back to the "all
  // open" defaults on first visit and if storage is unavailable.
  // Phase 27.12 — added 'compliance' section, deep-link via ?tab=…
  const [searchParams] = useSearchParams();
  const deepLinkTab = searchParams.get('tab');
  // Tab-based workspace. Map legacy ?tab= deep-links (from dashboard
  // Required-Action links) onto the new consolidated tabs.
  const TAB_MAP = {
    personal: 'overview', contact: 'overview', overview: 'overview',
    compliance: 'compliance', contracts: 'contracts',
    certifications: 'certifications', training: 'certifications',
    payment: 'accounting', accounting: 'accounting',
    documents: 'documents', sms: 'communications', communications: 'communications',
    onboarding: 'overview',
  };
  const [activeTab, setActiveTab] = useState(() => TAB_MAP[deepLinkTab] || 'overview');
  useEffect(() => {
    if (deepLinkTab && TAB_MAP[deepLinkTab]) setActiveTab(TAB_MAP[deepLinkTab]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTab]);

  // Catalog data — passed as props to AddContractDialog.
  const [carriers, setCarriers] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [levels, setLevels] = useState([]);

  // Dialog visibility flags. Each dialog owns its own internal form state.
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  // Phase 27.1 — the same dialog is reused in 'regenerate' mode when the
  // agent already has a linked user and access needs to be reset.
  const [accessDialogMode, setAccessDialogMode] = useState('generate');
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [showChangeNpnDialog, setShowChangeNpnDialog] = useState(false);
  const [showDeleteAppointmentDialog, setShowDeleteAppointmentDialog] = useState(false);
  const [showDeleteRequestDialog, setShowDeleteRequestDialog] = useState(false);
  const [showUserManageDialog, setShowUserManageDialog] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showUpdateInfoDialog, setShowUpdateInfoDialog] = useState(false);

  // Selected records for CRUD dialogs (parent-owned because they're chosen
  // from tables; the dialogs themselves are stateless containers for them).
  const [editingContract, setEditingContract] = useState(null);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [requestToDelete, setRequestToDelete] = useState(null);

  const isKR = isKRRole(user);
  const isAccounting = isAccountingRole(user);
  // Phase 27.19 — Split payment visibility into two tiers:
  //   • canSeeSensitivePaymentInfo → bank/tax details, KR+Accounting only
  //   • canSeePaymentInfo          → the whole Payment accordion, now
  //     also visible to the agent themselves and to agency principal/
  //     staff whose agency owns this agent so they can review paid
  //     statements + ledger totals.
  // User shape here: user.roles is an array of {role, agency_id, npn?, ...}
  // — there is no top-level user.agency_id / user.role scalar, so we walk
  // the roles list to determine ownership.
  const AGENCY_OWNER_ROLES = new Set(['Agency_Principal', 'Agency_Staff', 'Agency_User', 'Manager']);
  const canSeeSensitivePaymentInfo = isKR || isAccounting;
  const isSelfAgent = !!user?.agent_npn && String(user.agent_npn) === String(npn);
  const isOwningAgency = !!agent?.agency_id
    && (user?.roles || []).some((r) => AGENCY_OWNER_ROLES.has(r?.role) && String(r?.agency_id) === String(agent.agency_id));
  const canSeePaymentInfo = canSeeSensitivePaymentInfo || isSelfAgent || isOwningAgency;
  // When a non-KR agent lands on their OWN profile they must NOT see the
  // internal admin workspace (agent-channel SMS composer, internal CRM
  // notes, admin activity timeline). Those are KR-only operational tools.
  const isAgentSelfView = isSelfAgent && !isKR && !isAccounting;

  // Phase 27.8 — unified Contracts view. Merge `contract_requests` and
  // `carrier_appointments` into one list of rows. Requests are the primary
  // source (they carry the lifecycle status); each row is enriched with any
  // matching appointment (writing_number, appointed/terminated dates,
  // notes_internal) so the same row can drive both add and manage flows.
  const unifiedContracts = useMemo(() => {
    const apptIndex = new Map();
    appointments.forEach((a) => {
      const key = a.request_id
        || `${a.carrier_id}::${a.contract_type_id || ''}::${a.npn}`;
      apptIndex.set(key, a);
    });
    const seenAppointmentIds = new Set();
    const rows = requests.map((r) => {
      const appt = apptIndex.get(r.request_id)
        || apptIndex.get(`${r.carrier_id}::${r.contract_type_id || ''}::${r.npn}`);
      if (appt) seenAppointmentIds.add(appt.appointment_id);
      return {
        row_id: r.request_id,
        request_id: r.request_id,
        appointment_id: appt?.appointment_id,
        carrier_id: r.carrier_id,
        carrier_name: r.carrier_name,
        contract_type_id: r.contract_type_id,
        contract_type_name: r.contract_type_name,
        level_id: appt?.level_id || r.requested_level_id,
        level_name: appt?.level_name || r.requested_level_name,
        status: r.current_status,
        status_reason: r.current_status_reason || '',
        writing_number: r.writing_number || appt?.writing_number,
        appointed_date: r.appointed_date || appt?.appointed_date,
        terminated_date: r.terminated_date || appt?.terminated_date,
        termination_reason: r.termination_reason || appt?.termination_reason,
        request_notes: r.request_notes,
        notes_internal: appt?.notes_internal,
      };
    });
    // Appointments without a matching request (legacy direct appointments)
    // still deserve a row so nothing gets hidden after the merge.
    appointments
      .filter((a) => !seenAppointmentIds.has(a.appointment_id))
      .forEach((a) => rows.push({
        row_id: a.appointment_id,
        appointment_id: a.appointment_id,
        carrier_id: a.carrier_id,
        carrier_name: a.carrier_name,
        contract_type_id: a.contract_type_id,
        contract_type_name: a.contract_type_name,
        level_id: a.level_id,
        level_name: a.level_name,
        status: a.appointment_status,
        writing_number: a.writing_number,
        appointed_date: a.appointed_date,
        terminated_date: a.terminated_date,
        termination_reason: a.termination_reason,
        notes_internal: a.notes_internal,
      }));
    return rows;
  }, [requests, appointments]);

  useEffect(() => { loadAgentData(); }, [npn]);

  const loadAgentData = async () => {
    try {
      const agentRes = await agentAPI.get(npn);
      setAgent(agentRes.data);

      const [agenciesRes, appointmentsRes, requestsRes, hierarchyRes] = await Promise.all([
        agencyAPI.list(),
        appointmentAPI.getAgentAppointments(npn),
        contractRequestAPI.list({ npn }),
        agentAPI.getHierarchy(npn).catch(() => ({ data: null })),
      ]);

      setAgencies(agenciesRes.data);
      setAgency(agenciesRes.data.find((a) => a.agency_id === agentRes.data.agency_id));
      setAppointments(appointmentsRes.data);
      setRequests(requestsRes.data);
      setHierarchy(hierarchyRes.data);

      // KR-only catalogs used by AddContractDialog.
      if (isKRRole(user)) {
        const [carriersRes, typesRes, levelsRes] = await Promise.all([
          carrierAPI.list(),
          contractTypeAPI.list(),
          levelAPI.list(),
        ]);
        setCarriers(carriersRes.data);
        setContractTypes(typesRes.data);
        setLevels(levelsRes.data);
      }

      // Linked user account for the Administrative Actions block.
      try {
        const linkedRes = await agentAPI.getLinkedUser(npn);
        setLinkedUser(linkedRes.data.linked ? linkedRes.data.user : null);
      } catch {
        setLinkedUser(null);
      }
    } catch {
      toast.error('Failed to load agent data');
      navigate('/agents');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!agent) return null;

  return (
    <div data-testid="agent-profile-page">
      <PageHeader
        title="Agent Profile"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Agents', href: '/agents' },
          { label: `${agent.first_name} ${agent.last_name}`, href: `/agents/${npn}` },
        ]}
        actions={
          <Button variant="outline" onClick={() => navigate('/agents')}>
            <ArrowLeft size={16} className="mr-2" />
            Back to Agents
          </Button>
        }
      />

      <div className="page-content">
        {/* Getting Started Checklist — only surfaces during the onboarding
            lifecycle stage. Active agents don't need setup guidance. */}
        {agent.agent_status === 'onboarding' && (
          <div className="mb-6 space-y-4">
            {isKR ? (
              <OnboardingPanel npn={agent.npn} onChanged={loadAgentData} />
            ) : (
              <GettingStartedChecklist npn={agent.npn} />
            )}
          </div>
        )}

        {/* ═══════════ AGENT OVERVIEW HEADER (extracted, Phase 15) ═══════════ */}
        <BirthdayBanner agent={agent} />
        <AgentHeader
          agent={agent}
          agency={agency}
          linkedUser={linkedUser}
          isKR={isKR}
          onPhotoChanged={loadAgentData}
          onEdit={() => setShowEditDialog(true)}
          onTransfer={() => setShowTransferDialog(true)}
          onAddContract={() => { setEditingContract(null); setShowContractDialog(true); }}
          onInvite={() => { setAccessDialogMode('generate'); setShowInviteDialog(true); }}
          onManageUser={() => setShowUserManageDialog(true)}
          onToggleUserStatus={() => setShowDisableConfirm(true)}
          onChangeNpn={() => setShowChangeNpnDialog(true)}
          onRegenerateAccess={() => { setAccessDialogMode('regenerate'); setShowInviteDialog(true); }}
          onUpdateInfo={() => setShowUpdateInfoDialog(true)}
          onLeadSmsChanged={loadAgentData}
        />

        {/* Phase 17.1 — high-signal snapshot strip stays visible so
            operators see contract / cert / portal state at a glance. */}
        <div className="mb-4">
          <AgentSnapshot
            agent={agent}
            agency={agency}
            hierarchy={hierarchy}
            appointments={appointments}
            requests={requests}
            linkedUser={linkedUser}
          />
        </div>

        {/* ═══════════ TAB WORKSPACE ═══════════
            Reorganized (not rebuilt): every existing block is preserved,
            grouped into scannable tabs instead of one long scroll. Each
            piece of data appears in exactly one tab. */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="sticky top-0 z-20 bg-slate-50/95 backdrop-blur-sm py-1.5 mb-4 border-b border-slate-200">
            <TabsList className="w-full justify-start flex-nowrap overflow-x-auto h-auto" data-testid="agent-profile-tabs">
              <TabsTrigger value="overview" data-testid="agent-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="contracts" data-testid="agent-tab-contracts">Contracts</TabsTrigger>
              <TabsTrigger value="compliance" data-testid="agent-tab-compliance">Compliance</TabsTrigger>
              <TabsTrigger value="certifications" data-testid="agent-tab-certifications">Certifications</TabsTrigger>
              {!isAgentSelfView && (
                <TabsTrigger value="communications" data-testid="agent-tab-communications">Communications</TabsTrigger>
              )}
              {canSeePaymentInfo && (
                <TabsTrigger value="accounting" data-testid="agent-tab-accounting">Accounting</TabsTrigger>
              )}
              <TabsTrigger value="documents" data-testid="agent-tab-documents">Documents</TabsTrigger>
            </TabsList>
          </div>

          {/* ── OVERVIEW ── identity + short activity (summary strip above) ── */}
          <TabsContent value="overview" className="space-y-5 mt-0" data-testid="agent-overview-tab">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="border rounded-lg bg-white p-4">
                <h3 className="text-base font-heading font-semibold mb-3">Personal Information</h3>
                <AgentInformation
                  agent={agent}
                  agency={agency}
                  user={user}
                  onEdit={() => setShowEditDialog(true)}
                  sections={['personal', 'management']}
                  bare
                />
              </div>
              <div className="border rounded-lg bg-white p-4">
                <h3 className="text-base font-heading font-semibold mb-3">Contact Information</h3>
                <AgentInformation
                  agent={agent}
                  agency={agency}
                  user={user}
                  onEdit={() => setShowEditDialog(true)}
                  sections={['basic', 'address']}
                  bare
                />
              </div>
            </div>
            {!isAgentSelfView && (
              <div className="border rounded-lg bg-white p-4" data-testid="agent-section-timeline">
                <h3 className="text-base font-heading font-semibold mb-3">Recent Activity</h3>
                <AgentActivityTimeline npn={npn} />
              </div>
            )}
          </TabsContent>

          {/* ── CONTRACTS ── compact carrier portfolio table ── */}
          <TabsContent value="contracts" className="mt-0" data-testid="agent-contracts-tab">
            <div className="border rounded-lg bg-white p-4">
              <AgentContractsTable
                contracts={unifiedContracts}
                isKR={isKR}
                onAdd={() => { setEditingContract(null); setShowContractDialog(true); }}
                onEdit={(row) => {
                  if (row.request_id) {
                    navigate(`/contracts/${row.request_id}`);
                  } else {
                    setEditingContract(row);
                    setShowContractDialog(true);
                  }
                }}
                onDelete={(row) => {
                  if (row.appointment_id) {
                    setAppointmentToDelete(row); setShowDeleteAppointmentDialog(true);
                  } else if (row.request_id) {
                    setRequestToDelete(row); setShowDeleteRequestDialog(true);
                  }
                }}
              />
            </div>
          </TabsContent>

          {/* ── COMPLIANCE ── E&O / license / AHIP / FFM ── */}
          <TabsContent value="compliance" className="mt-0" data-testid="agent-compliance-tab">
            <div className="border rounded-lg bg-white p-4">
              <SmartComplianceTracker npn={npn} />
            </div>
          </TabsContent>

          {/* ── CERTIFICATIONS ── carrier cert progress + training/badges ── */}
          <TabsContent value="certifications" className="mt-0 space-y-5" data-testid="agent-certifications-tab">
            <div className="border rounded-lg bg-white p-4">
              <AepCertificationsTab agentNpn={npn} readOnly />
            </div>
            <div className="border rounded-lg bg-white p-4">
              <h3 className="text-base font-heading font-semibold mb-3">Training &amp; Badges</h3>
              <AgentAcademyBadges npn={npn} />
            </div>
          </TabsContent>

          {/* ── COMMUNICATIONS ── SMS + Notes + HubSpot (KR/agency only) ── */}
          {!isAgentSelfView && (
            <TabsContent value="communications" className="mt-0 space-y-5" data-testid="agent-communications-tab">
              <div className="border rounded-lg bg-white p-4">
                <h3 className="text-base font-heading font-semibold mb-3 flex items-center gap-2">
                  <MessageSquare size={16} /> SMS Conversation
                  <span className="text-xs font-normal text-slate-500">(canal Agent · +1 813 701-2300)</span>
                </h3>
                {(() => {
                  const smsUserId = agent?.user_id || linkedUser?.user_id;
                  const smsPhone = (
                    linkedUser?.sms_consent_phone
                    || linkedUser?.phone_normalized
                    || linkedUser?.phone
                    || agent?.phone
                    || agent?.mobile
                    || null
                  );
                  return smsUserId ? (
                    <ContactSmsCard
                      contactType="agent_user"
                      contactId={smsUserId}
                      phone={smsPhone}
                    />
                  ) : (
                    <div className="p-4 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded">
                      This agent does not yet have a KR Agency Hub user account.
                      Invite them first so SMS conversations, notifications, and
                      reminders can be threaded here.
                    </div>
                  );
                })()}
              </div>
              <div className="border rounded-lg bg-white p-4" data-testid="agent-section-notes">
                <h3 className="text-base font-heading font-semibold mb-3">Notes &amp; Communication History</h3>
                <CRMNotesPanel
                  subjectType="agent"
                  subjectId={npn}
                  subjectAgencyId={agent?.agency_id}
                  canCreateNote={!isAgent(user)}
                />
              </div>
              {isKR && (
                <div className="border rounded-lg bg-white p-4" data-testid="agent-section-hubspot">
                  <h3 className="text-base font-heading font-semibold mb-3 flex items-center gap-2">
                    <ExternalLink size={16} /> HubSpot Sync
                  </h3>
                  <HubspotSyncCard npn={npn} />
                </div>
              )}
            </TabsContent>
          )}

          {/* ── ACCOUNTING ── payment info / W-9 / EFT / history / ledger ── */}
          {canSeePaymentInfo && (
            <TabsContent value="accounting" className="mt-0 space-y-4" data-testid="agent-accounting-tab">
              {canSeeSensitivePaymentInfo && (
                <PaymentInformationCard
                  key={`pi-${paymentInfoRefreshKey}`}
                  recipientType="agent"
                  recipientId={agent.user_id || agent.npn}
                />
              )}
              <PayeeEftCard
                payeeType="Agent"
                payeeId={agent.npn}
                recipientType="agent"
                recipientId={agent.user_id || agent.npn}
                canView={canSeePaymentInfo}
                canManage={canSeeSensitivePaymentInfo}
                onDirectDepositSync={() => setPaymentInfoRefreshKey((k) => k + 1)}
              />
              <PaymentHistoryCard
                recipientType="agent"
                recipientId={agent.user_id || agent.npn}
              />
              <RecipientStatementsCard
                recipientType="agent"
                recipientId={agent.user_id || agent.npn}
              />
              <RecipientAccountingSection
                recipientType="agent"
                recipientId={agent.user_id || agent.npn}
              />
            </TabsContent>
          )}

          {/* ── DOCUMENTS ── files + communication exception ── */}
          <TabsContent value="documents" className="mt-0 space-y-5" data-testid="agent-documents-tab">
            <div className="border rounded-lg bg-white p-4">
              <AgentDocumentsTab npn={npn} />
            </div>
            <AgentCommunicationException agent={agent} />
          </TabsContent>
        </Tabs>
      </div>

      {/* ═══════════ DIALOGS (Phase 17 + 17.7 refactor) ═══════════ */}
      <EditAgentDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        agent={agent}
        user={user}
        onSaved={loadAgentData}
      />

      <TransferAgentDialog
        open={showTransferDialog}
        onOpenChange={setShowTransferDialog}
        agent={agent}
        agency={agency}
        agencies={agencies}
        onTransferred={loadAgentData}
      />

      <ContractManageDialog
        open={showContractDialog}
        onOpenChange={setShowContractDialog}
        agent={agent}
        editing={editingContract}
        carriers={carriers}
        contractTypes={contractTypes}
        levels={levels}
        requests={requests}
        appointments={appointments}
        onSaved={loadAgentData}
      />

      <DeleteAppointmentDialog
        open={showDeleteAppointmentDialog}
        onOpenChange={setShowDeleteAppointmentDialog}
        appointment={appointmentToDelete}
        agentNpn={npn}
        onDeleted={() => { setAppointmentToDelete(null); loadAgentData(); }}
      />

      <DeleteRequestDialog
        open={showDeleteRequestDialog}
        onOpenChange={setShowDeleteRequestDialog}
        request={requestToDelete}
        onDeleted={() => { setRequestToDelete(null); loadAgentData(); }}
      />

      <ChangeNPNDialog
        open={showChangeNpnDialog}
        onOpenChange={setShowChangeNpnDialog}
        agent={agent}
      />

      <GenerateAccessDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        agent={agent}
        linkedUser={linkedUser}
        mode={accessDialogMode}
        onGenerated={() => { loadAgentData(); }}
      />

      <ManageUserDialog
        open={showUserManageDialog}
        onOpenChange={setShowUserManageDialog}
        linkedUser={linkedUser}
        agencies={agencies}
        agent={agent}
        onUpdated={loadAgentData}
        onRequestToggleStatus={() => setShowDisableConfirm(true)}
      />

      <DisableEnableAccessDialog
        open={showDisableConfirm}
        onOpenChange={setShowDisableConfirm}
        linkedUser={linkedUser}
        onUpdated={loadAgentData}
      />

      <UpdateInfoLinkDialog
        open={showUpdateInfoDialog}
        onOpenChange={setShowUpdateInfoDialog}
        agent={agent}
      />
    </div>
  );
}
